/**
 * The live captions view (SEO_BRIEF_09 section 2.5): Brief 08's live mode (lib/live-speech.ts, the
 * browser's own speech recognition) shown as large captions instead of a text box, on
 * /live-captions (tools/_live_captions.html, mounted by pages/mic.ts).
 *
 * WHAT IS ON SCREEN. Large text, the newest words at the bottom, and only the last 2 to 4 lines:
 * the window is that many lines high and pinned to its bottom edge (site.css, `.captions__window`),
 * so older lines leave at the top. Which size shows how many lines is CAPTION_SIZES. Only the tail
 * of the session is drawn (`captionTail`), so a long session costs no more to draw than a short one;
 * the whole session is kept for Download .txt.
 *
 * THE SAME ENGINE AS LIVE TYPING. `createLive` starts recognition in the language picked, restarts
 * it after a silence while it is on, and switches off with a reason on an error that won't clear.
 * Nothing here is a second copy of that logic.
 *
 * THE SCREEN. A Screen Wake Lock is held while captions run, taken again when the page comes back
 * into view, and let go on Stop; a browser without one shows one line. Full screen fills the window
 * at once and asks the Fullscreen API to make that the whole screen; where the browser has none,
 * refuses or never answers (Safari on an iPhone, an embedded view), the view still fills the
 * window, and Escape or the button leaves either.
 *
 * THE AUDIO NEVER REACHES OUR SERVER. The browser sends it to its own vendor's speech service and
 * hands back text. The one request this view makes is the `live_captions_start` beacon
 * (`countCaptionsStart`, through the `onStart` it is handed), and the daily minutes are not
 * touched.
 *
 * The decisions live in `createCaptions`, which draws through a `CaptionsView`, so they are tested
 * without a DOM (web/test/live-captions.test.ts); `mountLiveCaptions` is that view on the page.
 */

import { qs, qsa } from './dom.ts';
import {
  appendFinal,
  createLive,
  interimTail,
  type Live,
  type LiveFailure,
  pickLanguage,
  type RecognitionCtor,
  recognitionCtor,
} from './live-speech.ts';
import { browserRecorderEnv, saveBlob } from './mic-panel.ts';
import type { WakeLockLike } from './recorder.ts';
import { countUse, TOOL_SLUG } from './usage.ts';

/* ── what the view shows ───────────────────────────────────────────────────── */

export interface CaptionSize {
  /** The text's size, as a multiple of the view's base size (site.css `--cap-base`). */
  readonly scale: number;
  /** How many lines are on screen at that size. */
  readonly lines: number;
}

/**
 * The four text sizes, smallest first. Bigger text shows fewer lines, so the window stays about
 * the same height at every size.
 */
export const CAPTION_SIZES: readonly CaptionSize[] = [
  { scale: 1, lines: 4 },
  { scale: 1.3, lines: 4 },
  { scale: 1.7, lines: 3 },
  { scale: 2.2, lines: 2 },
];

/** The size a visit starts at: the second, four lines. */
export const DEFAULT_SIZE_STEP = 1;

/**
 * The most of the session drawn at once. Four lines at the smallest size across a wide screen are
 * a few hundred characters; this is several times that, so the window is always full.
 */
export const TAIL_CHARS = 1200;

export type CaptionTheme = 'dark' | 'light';

/** The status line's keys: the template's `[data-captions-label]` words. */
export type CaptionStatus = 'listening' | 'stopped' | 'blocked' | 'no-mic' | 'network' | 'language';

const FAILURE_STATUS: Record<LiveFailure, CaptionStatus> = {
  blocked: 'blocked',
  'no-mic': 'no-mic',
  network: 'network',
  language: 'language',
  failed: 'stopped',
};

/** A step in CAPTION_SIZES, whatever number it was handed. */
export function clampSizeStep(step: number): number {
  if (!Number.isFinite(step)) return DEFAULT_SIZE_STEP;
  return Math.min(CAPTION_SIZES.length - 1, Math.max(0, Math.round(step)));
}

/**
 * The end of the session's text that is drawn: all of it when it is short, else the last `max`
 * characters from the start of a word, so no half word shows. Text with no spaces (Chinese,
 * Japanese, Thai) is cut at `max`.
 */
export function captionTail(text: string, max: number = TAIL_CHARS): string {
  if (text.length <= max) return text;
  const tail = text.slice(text.length - max);
  const space = tail.search(/\s/);
  if (space < 0 || space > max / 4) return tail;
  return tail.slice(space).trimStart();
}

/** The download's name: `live-captions-2026-10-22-1432.txt`, the time the visitor saved it. */
export function captionsFileName(at: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  return `live-captions-${day}-${pad(at.getHours())}${pad(at.getMinutes())}.txt`;
}

/* ── the view's decisions ──────────────────────────────────────────────────── */

/** What `createCaptions` draws with. The page's is `mountLiveCaptions`; the tests' is a record. */
export interface CaptionsView {
  /** The words on screen: the end of the settled text, then the words still being guessed. */
  render(settled: string, guess: string): void;
  /** Captions switched on or off. */
  running(on: boolean): void;
  size(step: number, size: CaptionSize): void;
  theme(theme: CaptionTheme): void;
  /** The view fills the screen (the Fullscreen API) or the window (where it has none). */
  filling(on: boolean): void;
  /** The status line, or none. */
  status(key: CaptionStatus | null): void;
  /** The line that says the screen may turn off: shown while running without a wake lock. */
  screenLine(show: boolean): void;
  /** Whether there is a session to download or clear. */
  hasText(any: boolean): void;
  /** No speech recognition in this browser (Firefox): the one line instead of the view. */
  unsupported(): void;
}

/** The Fullscreen API for the view, as `fullscreenFor` makes it. */
export interface FullscreenLike {
  /** Ask for full screen. Rejects where the browser refuses. */
  enter(): Promise<void>;
  exit(): Promise<void>;
  /** Whether the view is full screen now. */
  readonly active: boolean;
}

export interface CaptionsDeps {
  /** The browser's recognition; null where it has none. */
  recognition: RecognitionCtor | null;
  view: CaptionsView;
  /** `navigator.wakeLock.request('screen')`, where the browser has it. */
  requestWakeLock?: (() => Promise<WakeLockLike>) | undefined;
  /** Calls `fn` whenever the page becomes visible again; returns the unsubscribe. */
  onVisible?: ((fn: () => void) => () => void) | undefined;
  /** The Fullscreen API for the view, or null where the browser has none. */
  fullscreen?: FullscreenLike | null;
  /** Save a file from this tab (mic-panel's `saveBlob`). */
  save(blob: Blob, filename: string): void;
  /** Captions were started by the visitor, in `lang`: the `live_captions_start` beacon. */
  onStart?(lang: string): void;
  /** A save made (`download` returned true): the page tells the rating card (lib/usage.ts). */
  onSave?(): void;
  /** The recognition's clock (live-speech's quick-restart check). */
  now?: () => number;
  /** The download's timestamp. */
  clock?: () => Date;
}

export interface Captions {
  /** False where the browser has no speech recognition. */
  readonly supported: boolean;
  readonly on: boolean;
  /** The whole session's settled words, as Download .txt saves them. */
  readonly text: string;
  readonly sizeStep: number;
  readonly theme: CaptionTheme;
  readonly filling: boolean;
  start(lang: string): void;
  stop(): void;
  /** A new language: captions that are running start again in it. */
  setLanguage(lang: string): void;
  setSize(step: number): void;
  setTheme(theme: CaptionTheme): void;
  toggleTheme(): void;
  /** Full screen on or off. Resolves once the view has changed. */
  toggleFullscreen(): Promise<void>;
  leaveFullscreen(): Promise<void>;
  /** The browser's `fullscreenchange`: Escape, or the browser leaving full screen by itself. */
  fullscreenChanged(active: boolean): void;
  /** Save the session as a .txt. False when there is nothing to save. */
  download(): boolean;
  clear(): void;
}

export function createCaptions(deps: CaptionsDeps): Captions {
  const { view } = deps;
  const clock = deps.clock ?? (() => new Date());
  let settled = '';
  let guess = '';
  let step = DEFAULT_SIZE_STEP;
  let theme: CaptionTheme = 'dark';
  let filling = false;
  /** Full screen came from the Fullscreen API, not from filling the window. */
  let viaApi = false;
  let wake: WakeLockLike | null = null;
  let unwatch: (() => void) | null = null;
  let live: Live | null = null;

  const paint = () => view.render(captionTail(settled), interimTail(settled, guess));

  async function holdScreen(): Promise<void> {
    if (!deps.requestWakeLock) {
      view.screenLine(true);
      return;
    }
    if (wake) return;
    try {
      const sentinel = await deps.requestWakeLock();
      if (!live?.on || wake) {
        void sentinel.release().catch(() => undefined);
        return;
      }
      wake = sentinel;
      // The browser lets go by itself when the page is hidden; `onVisible` takes it again.
      sentinel.addEventListener?.('release', () => {
        if (wake === sentinel) wake = null;
      });
      view.screenLine(false);
    } catch {
      // Refused (a battery saver, a hidden tab): the line says the screen may turn off.
      view.screenLine(true);
    }
  }

  function letScreenGo(): void {
    unwatch?.();
    unwatch = null;
    const held = wake;
    wake = null;
    if (held) void held.release().catch(() => undefined);
    view.screenLine(false);
  }

  if (deps.recognition) {
    live = createLive(
      deps.recognition,
      {
        onInterim: (words) => {
          guess = words;
          paint();
        },
        onFinal: (words) => {
          settled = appendFinal(settled, words);
          view.hasText(settled.trim() !== '');
          paint();
        },
        onSwitch: (on) => {
          view.running(on);
          // A failure's own line follows this one (onFail), so a stop it caused says why.
          view.status(on ? 'listening' : 'stopped');
          if (on) {
            if (!unwatch && deps.onVisible) {
              unwatch = deps.onVisible(() => {
                if (live?.on) void holdScreen();
              });
            }
            void holdScreen();
          } else {
            letScreenGo();
          }
        },
        onFail: (failure) => view.status(FAILURE_STATUS[failure]),
      },
      deps.now,
    );
  }

  function setTheme(next: CaptionTheme): void {
    theme = next === 'light' ? 'light' : 'dark';
    view.theme(theme);
  }

  function setFilling(on: boolean): void {
    filling = on;
    view.filling(on);
  }

  async function leaveFullscreen(): Promise<void> {
    if (!filling) return;
    const fs = deps.fullscreen;
    if (viaApi && fs?.active) {
      try {
        await fs.exit();
      } catch {
        /* already left */
      }
    }
    viaApi = false;
    setFilling(false);
  }

  view.size(step, CAPTION_SIZES[step] as CaptionSize);
  view.theme(theme);
  view.hasText(false);
  paint();
  if (!live) view.unsupported();

  return {
    get supported() {
      return live !== null;
    },
    get on() {
      return live?.on ?? false;
    },
    get text() {
      return settled;
    },
    get sizeStep() {
      return step;
    },
    get theme() {
      return theme;
    },
    get filling() {
      return filling;
    },
    start(lang) {
      if (!live || live.on) return;
      live.start(lang);
      if (live.on) deps.onStart?.(lang);
    },
    stop() {
      live?.stop();
    },
    setLanguage(lang) {
      if (!live?.on) return;
      live.stop();
      live.start(lang);
    },
    setSize(next) {
      step = clampSizeStep(next);
      view.size(step, CAPTION_SIZES[step] as CaptionSize);
    },
    setTheme,
    toggleTheme() {
      setTheme(theme === 'dark' ? 'light' : 'dark');
    },
    async toggleFullscreen() {
      if (filling) {
        await leaveFullscreen();
        return;
      }
      // The view fills the window at once; the Fullscreen API, when the browser agrees, makes that
      // the whole screen. A browser that refuses, or never answers (an embedded view), leaves the
      // view filling the window rather than a button that seems to do nothing.
      viaApi = false;
      setFilling(true);
      const fs = deps.fullscreen;
      if (!fs) return;
      try {
        await fs.enter();
        if (filling) viaApi = true;
      } catch {
        // Refused, or not for this element: the view keeps filling the window.
      }
    },
    leaveFullscreen,
    fullscreenChanged(active) {
      if (active) {
        viaApi = true;
        if (!filling) setFilling(true);
        return;
      }
      // Escape, or the browser leaving by itself: the view goes back into the page.
      if (viaApi) {
        viaApi = false;
        setFilling(false);
      }
    },
    download() {
      const words = settled.trim();
      if (!words) return false;
      // Made here, from the session: nothing is uploaded.
      deps.save(
        new Blob([`${words}\n`], { type: 'text/plain;charset=utf-8' }),
        captionsFileName(clock()),
      );
      deps.onSave?.();
      return true;
    },
    clear() {
      settled = '';
      guess = '';
      view.hasText(false);
      paint();
      if (!live?.on) view.status(null);
    },
  };
}

/* ── the page ──────────────────────────────────────────────────────────────── */

/**
 * The `live_captions_start` beacon (SEO_BRIEF_09 section 2.7): captions switched on by the visitor,
 * under the page's slug, in the language picked (a BCP 47 tag from Brief 08's list, `en-US`).
 * A silence restart or a language change is not a start. Its format is `txt`, live typing's.
 */
export function countCaptionsStart(tool: string, lang: string): void {
  if (TOOL_SLUG.test(tool)) countUse('live_captions_start', tool, 'txt', { lang });
}

type WebkitDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | undefined;
};
type WebkitElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | undefined;
};

/** The Fullscreen API for `el`, under its standard name or Safari's; null where there is none. */
export function fullscreenFor(el: HTMLElement, doc: Document = document): FullscreenLike | null {
  const d = doc as WebkitDocument;
  const target = el as WebkitElement;
  const request =
    typeof target.requestFullscreen === 'function'
      ? () => target.requestFullscreen()
      : typeof target.webkitRequestFullscreen === 'function'
        ? () => target.webkitRequestFullscreen?.()
        : null;
  if (!request) return null;
  return {
    async enter() {
      await request();
    },
    async exit() {
      if (typeof d.exitFullscreen === 'function') await d.exitFullscreen();
      else await d.webkitExitFullscreen?.();
    },
    get active() {
      return (d.fullscreenElement ?? d.webkitFullscreenElement ?? null) === el;
    },
  };
}

export interface LiveCaptionsOptions {
  /** The `live_captions_start` beacon, given the language captions started in. */
  onStart?(lang: string): void;
  /** A save made: no beacon, the rating card is told (pages/mic.ts `noteResult`). */
  onSave?(): void;
  /** The browser's pieces, replaceable when the page is driven by a test. */
  recognition?: RecognitionCtor | null;
  language?: string;
}

/** The captions view on `root` (`[data-captions]`, tools/_live_captions.html). */
export function mountLiveCaptions(root: HTMLElement, options: LiveCaptionsOptions = {}): Captions {
  const viewEl = qs<HTMLElement>('[data-captions-view]', root) ?? root;
  const toggle = qs<HTMLButtonElement>('[data-captions-toggle]', root);
  const langSelect = qs<HTMLSelectElement>('[data-captions-lang]', root);
  const sizeSelect = qs<HTMLSelectElement>('[data-captions-size]', root);
  const themeBtn = qs<HTMLButtonElement>('[data-captions-theme-toggle]', root);
  const fullBtn = qs<HTMLButtonElement>('[data-captions-full]', root);
  const status = qs<HTMLElement>('[data-captions-status]', root);
  const screen = qs<HTMLElement>('[data-captions-screen]', root);
  const windowEl = qs<HTMLElement>('[data-captions-window]', root);
  const said = qs<HTMLElement>('[data-captions-said]', root);
  const guessEl = qs<HTMLElement>('[data-captions-guess]', root);
  const empty = qs<HTMLElement>('[data-captions-empty]', root);
  const saveBtn = qs<HTMLButtonElement>('[data-captions-download]', root);
  const clearBtn = qs<HTMLButtonElement>('[data-captions-clear]', root);
  const wakeLine = qs<HTMLElement>('[data-captions-wake]', root);
  const none = qs<HTMLElement>('[data-captions-none]', root);

  /** Show the spans of `scope` whose `attr` is `value`, and hide the rest. */
  const showWhen = (scope: HTMLElement | null, attr: string, value: string) => {
    if (!scope) return;
    for (const span of qsa<HTMLElement>(`[${attr}]`, scope)) {
      span.hidden = span.getAttribute(attr) !== value;
    }
  };

  const say = (key: CaptionStatus | null) => {
    if (!status) return;
    const source = key ? qs<HTMLElement>(`[data-captions-label="${key}"]`, root) : null;
    status.replaceChildren(
      ...(source ? Array.from(source.childNodes, (n) => n.cloneNode(true)) : []),
    );
  };

  const view: CaptionsView = {
    render(text, guess) {
      if (said) said.textContent = text;
      if (guessEl) guessEl.textContent = guess;
      if (empty) empty.hidden = Boolean(text || guess);
    },
    running(on) {
      toggle?.setAttribute('aria-pressed', on ? 'true' : 'false');
      showWhen(toggle, 'data-captions-when', on ? 'on' : 'off');
      root.classList.toggle('is-listening', on);
    },
    size(step, size) {
      windowEl?.style.setProperty('--cap-scale', String(size.scale));
      windowEl?.style.setProperty('--cap-lines', String(size.lines));
      if (sizeSelect) sizeSelect.value = String(step);
    },
    theme(theme) {
      viewEl.dataset['captionsTheme'] = theme;
      showWhen(themeBtn, 'data-captions-theme-when', theme);
    },
    filling(on) {
      viewEl.classList.toggle('is-filling', on);
      document.documentElement.classList.toggle('has-captions-filling', on);
      showWhen(fullBtn, 'data-captions-full-when', on ? 'on' : 'off');
    },
    status: say,
    screenLine(show) {
      if (wakeLine) wakeLine.hidden = !show;
    },
    hasText(any) {
      for (const b of [saveBtn, clearBtn]) if (b) b.disabled = !any;
    },
    unsupported() {
      // Firefox, and any browser without speech recognition: one line, linking Record.
      viewEl.hidden = true;
      if (none) none.hidden = false;
    },
  };

  const env = browserRecorderEnv();
  const Recognition =
    options.recognition !== undefined ? options.recognition : recognitionCtor(globalThis);
  if (langSelect) {
    const offered = Array.from(langSelect.options, (o) => o.value);
    const preferred =
      options.language ?? (typeof navigator === 'undefined' ? undefined : navigator.language);
    langSelect.value = pickLanguage(preferred, offered);
  }
  const lang = () => langSelect?.value || 'en-US';
  const syncLang = () => {
    // The captions are in the language picked, for the browser's fonts and line breaking.
    screen?.setAttribute('lang', lang());
  };
  syncLang();

  const captions = createCaptions({
    recognition: Recognition,
    view,
    requestWakeLock: env.requestWakeLock,
    onVisible: env.onVisible,
    fullscreen: fullscreenFor(viewEl),
    save: saveBlob,
    ...(options.onStart ? { onStart: options.onStart } : {}),
    ...(options.onSave ? { onSave: options.onSave } : {}),
  });
  if (!captions.supported) return captions;

  toggle?.addEventListener('click', () => {
    if (captions.on) captions.stop();
    else captions.start(lang());
  });
  langSelect?.addEventListener('change', () => {
    syncLang();
    captions.setLanguage(lang());
  });
  sizeSelect?.addEventListener('change', () => captions.setSize(Number(sizeSelect.value)));
  themeBtn?.addEventListener('click', () => captions.toggleTheme());
  fullBtn?.addEventListener('click', () => void captions.toggleFullscreen());
  saveBtn?.addEventListener('click', () => captions.download());
  clearBtn?.addEventListener('click', () => {
    captions.clear();
    toggle?.focus();
  });
  const onChange = () => {
    const d = document as WebkitDocument;
    captions.fullscreenChanged(
      (d.fullscreenElement ?? d.webkitFullscreenElement ?? null) === viewEl,
    );
  };
  document.addEventListener('fullscreenchange', onChange);
  document.addEventListener('webkitfullscreenchange', onChange);
  // Escape leaves the window-filling view too; the Fullscreen API's own Escape is the browser's.
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && captions.filling) void captions.leaveFullscreen();
  });
  return captions;
}
